import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  HardHat,
  FileText,
  Users,
  type LucideIcon,
} from 'lucide-react';
import project from '@data/project.json';
import NewCustomerButton from '@components/NewCustomerButton';
import { useT, type StringKey } from '@lib/i18n';
import { useSessionStore } from '@stores/sessionStore';

interface GuaranteeView {
  id: string;
  title_ar: string;
  title_en: string;
  desc_ar: string;
  desc_en: string;
}

const GUARANTEE_IDS_TO_SHOW = ['escrow', 'construction', 'reports', 'owners_assoc'] as const;

const ICON_BY_ID: Record<(typeof GUARANTEE_IDS_TO_SHOW)[number], LucideIcon> = {
  escrow: ShieldCheck,
  construction: HardHat,
  reports: FileText,
  owners_assoc: Users,
};

export default function Step5Guarantees() {
  const t = useT();
  const navigate = useNavigate();
  const language = useSessionStore((s) => s.language);
  const isAr = language === 'ar';

  const cards = GUARANTEE_IDS_TO_SHOW.map((id) => {
    const g = (project.guarantees as GuaranteeView[]).find((x) => x.id === id);
    if (!g) return null;
    return { ...g, Icon: ICON_BY_ID[id] };
  }).filter((c): c is GuaranteeView & { Icon: LucideIcon } => c !== null);

  return (
    <div className="step-container">
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => navigate('/match')}
          className="btn-ghost !px-3 !py-1.5 text-sm"
        >
          {isAr ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
          {t('common.back')}
        </button>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-2 text-xs text-navy-400">
            <span className="w-1.5 h-1.5 rounded-full bg-gold" />
            {t('guar.timeBudget')}
            <span className="num-en">· 2 {t('common.minute')}</span>
          </span>
          <NewCustomerButton />
        </div>
      </div>

      <Breadcrumb t={t} isAr={isAr} />

      <header className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-navy mb-2">
          {t('guar.title')}
        </h1>
        <p className="text-navy-600">{t('guar.subtitle')}</p>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cards.map(({ id, Icon, title_ar, title_en, desc_ar, desc_en }) => (
          <article
            key={id}
            className="card p-5 md:p-6 flex gap-4 hover:border-gold-300 hover:shadow-md transition-all"
          >
            <div className="flex-shrink-0">
              <div className="w-14 h-14 rounded-full bg-gold-50 flex items-center justify-center">
                <Icon className="w-7 h-7 text-navy-700" strokeWidth={1.75} />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-navy mb-1">
                {isAr ? title_ar : title_en}
              </h3>
              <p className="text-sm text-navy-600 leading-relaxed">
                {isAr ? desc_ar : desc_en}
              </p>
            </div>
          </article>
        ))}
      </section>

      <p className="mt-6 text-xs text-navy-400 text-center leading-relaxed">
        {t('guar.delayProtectionNote')}
      </p>

      <div className="mt-8 flex flex-col-reverse md:flex-row md:justify-end items-stretch md:items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/match')}
          className="btn-ghost"
        >
          {isAr ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
          {t('common.back')}
        </button>
        <button
          type="button"
          onClick={() => navigate('/report')}
          className="btn-primary"
        >
          {t('guar.continueToReport')}
        </button>
      </div>
    </div>
  );
}

function Breadcrumb({
  t,
  isAr,
}: {
  t: (k: StringKey) => string;
  isAr: boolean;
}) {
  const sep = isAr ? '←' : '→';
  return (
    <nav
      aria-label="breadcrumb"
      className="flex items-center justify-center gap-2 text-xs text-navy-400 mb-6 flex-wrap"
    >
      <span>{t('match.crumbWelcome')}</span>
      <span>{sep}</span>
      <span>{t('match.crumbProject')}</span>
      <span>{sep}</span>
      <span>{t('match.crumbGoal')}</span>
      <span>{sep}</span>
      <span>{t('match.crumbMatch')}</span>
      <span>{sep}</span>
      <span className="text-navy-700 font-semibold">{t('guar.crumb')}</span>
    </nav>
  );
}
